import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    RefreshCw,
    FileText,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    ExternalLink,
    ArrowUpDown,
    ChevronUp,
    ChevronDown,
    MoreVertical,
    History,
} from "lucide-react";
import { toast } from "react-toastify";
import { db } from "../firebase";
import {
    collection,
    getDocs,
    query,
    where,
    doc,
    getDoc,
    updateDoc,
    addDoc,
} from "firebase/firestore";

/* ===== Config ===== */
const THRESHOLD = 500000;
const nfARS = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });

/* Historial global */
const GLOBAL_HISTORY_LIMIT = 250; // 👈 últimos N envíos (ordenado en front, sin índices)

/* Tasas y defaults — mismo criterio que la pantalla del cliente */
const IIBB_CABA_RATE_DEFAULT = 0.0;
const IIBB_ARBA_RATE_DEFAULT = 0.0;
const IVA_RATE = 0.21;
const PROMPT_PAY_RATE = 0.10;

const formatDateAR = (value) => {
    if (!value) return "—";
    try {
        const d =
            value?.toDate?.() instanceof Date
                ? value.toDate()
                : typeof value === "number"
                    ? new Date(value)
                    : new Date(String(value));
        return new Intl.DateTimeFormat("es-AR", {
            dateStyle: "short",
            timeStyle: "short",
            timeZone: "America/Argentina/Buenos_Aires",
        }).format(d);
    } catch {
        return "—";
    }
};

/** Normaliza un teléfono a solo dígitos */
const normalizeWaNumber = (raw = "") => String(raw).replace(/\D+/g, "");

/** Convierte distintos formatos de timestamp a ms (number) */
const toMs = (v) => {
    if (!v) return null;
    if (typeof v === "number") return v < 10 ** 12 ? v * 1000 : v;
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "string") {
        const t = Date.parse(v);
        return Number.isFinite(t) ? t : null;
    }
    if (typeof v === "object" && typeof v.seconds === "number") {
        return v.seconds * 1000 + (v.nanoseconds ? Math.floor(v.nanoseconds / 1e6) : 0);
    }
    return null;
};

/** Última fecha de cambio de estado */
const getLastStatusAt = (docData = {}) => {
    const candidates = [];
    if (Array.isArray(docData.statusHistory)) {
        for (const item of docData.statusHistory) {
            const t = toMs(item?.at || item?.ts || item?.timestamp || item?.date);
            if (t) candidates.push(t);
        }
    }
    const fallbacks = [toMs(docData.statusUpdatedAt), toMs(docData.updatedAt), toMs(docData.createdAt)].filter(Boolean);
    candidates.push(...fallbacks);
    return candidates.length ? Math.max(...candidates) : null;
};

/* ====== Validación de números ====== */

/** Solo dígitos (para CUIT y tel). */
const onlyDigits = (s = "") => String(s).replace(/\D+/g, "");

/** Valida formato internacional básico AR. */
const validateWaNumber = (raw = "") => {
    const clean = normalizeWaNumber(raw);
    const errors = [];
    if (!clean) errors.push("vacío");
    if (clean && !clean.startsWith("54")) errors.push("falta prefijo 54");
    if (clean && (clean.length < 11 || clean.length > 14)) errors.push("longitud inválida");
    return { valid: errors.length === 0, clean, errors };
};

/** Texto corto de errores para tooltip */
const describeNumberErrors = (errs = []) => (errs.length ? `Error: ${errs.join(" · ")}` : "");

/* === Helpers para filas pagadas (pendingOrdersPaid) === */

const getPaidTotal = (d = {}) => {
    const t =
        d?.totals?.total ??
        d?.totals?.totalToTransfer ??
        d?.totalToTransfer ??
        d?.finalAmountTransferred ??
        null;

    if (t != null) return Number(t);
    const items = Array.isArray(d.productos) ? d.productos : [];
    const sum = items.reduce((a, p) => {
        const precio = Number(p.precioUnitario || 0);
        const bonif = Number(p.bonificacion || 0) / 100;
        const qty = Number(p.cantidad || p.qty || 0);
        return a + Number((qty * precio * (1 - bonif)).toFixed(2));
    }, 0);
    return Number(sum);
};

const getPaidLastAt = (d = {}) =>
    toMs(d?.payment?.uploadedAt) ||
    toMs(d?.payment?.markedAt) ||
    toMs(d?.createdAt) ||
    toMs(d?.updatedAt);

const getPaidAt = (d = {}) =>
    toMs(d?.payment?.uploadedAt) ||
    toMs(d?.payment?.markedAt) ||
    toMs(d?.createdAt) ||
    toMs(d?.updatedAt);

function derivePaidTotals(d = {}) {
    if (d?.totals && typeof d.totals === "object") return d.totals;
    const items = Array.isArray(d.productos) ? d.productos : [];
    const sumBySub = items.reduce((a, p) => a + Number(p.subtotal || 0), 0);
    if (sumBySub > 0) {
        return {
            subtotal: Number(sumBySub.toFixed(2)),
            total: Number(sumBySub.toFixed(2)),
            totalToTransfer: Number(sumBySub.toFixed(2)),
        };
    }
    const sumByCalc = items.reduce((a, p) => {
        const precio = Number(p.precioUnitario || 0);
        const bonif = Number(p.bonificacion || 0) / 100;
        const qty = Number(p.cantidad || p.qty || 0);
        return a + Number((qty * precio * (1 - bonif)).toFixed(2));
    }, 0);
    return {
        subtotal: Number(sumByCalc.toFixed(2)),
        total: Number(sumByCalc.toFixed(2)),
        totalToTransfer: Number(sumByCalc.toFixed(2)),
    };
}

/** Mapea status */
const mapStatusUI = (statusRaw = "") => {
    const s = String(statusRaw).toLowerCase();

    const isAnuladoPorTiempo = s === "anuladoportiempo";
    const isPending = s === "pending";
    const isPendingPay = s === "pendingpayment" || s === "pendingpaymentcash";
    const isPaid = s === "paid" || s === "completed" || s === "approved";
    const isCanceled = s === "cancelled" || s === "canceled";

    if (isAnuladoPorTiempo) {
        return {
            label: "Anulado por tiempo",
            className: "border-purple-200 bg-purple-50 text-purple-700",
            Icon: XCircle,
            kind: "anuladoPorTiempo",
        };
    }
    if (s.includes("reviewtocheckout")) {
        return {
            label: "Pendiente de sumar productos",
            className: "border-blue-200 bg-blue-50 text-blue-700",
            Icon: AlertTriangle,
            kind: "reviewToCheckout",
        };
    }
    if (isCanceled) {
        return {
            label: "Cancelado",
            className: "border-red-200 bg-red-50 text-red-700",
            Icon: XCircle,
            kind: "canceled",
        };
    }
    if (isPaid) {
        return {
            label: "Pagado",
            className: "border-green-200 bg-green-50 text-green-700",
            Icon: CheckCircle2,
            kind: "paid",
        };
    }
    if (isPendingPay) {
        return {
            label: "Pendiente de transferencia",
            className: "border-amber-200 bg-amber-50 text-amber-700",
            Icon: AlertTriangle,
            kind: "pendingPay",
        };
    }
    if (isPending) {
        return {
            label: "Pendiente",
            className: "border-yellow-200 bg-yellow-50 text-yellow-700",
            Icon: AlertTriangle,
            kind: "pending",
        };
    }

    const label = s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").trim() || "—";
    return {
        label: label.charAt(0).toUpperCase() + label.slice(1),
        className: "border-neutral-200 bg-neutral-50 text-neutral-700",
        Icon: FileText,
        kind: "other",
    };
};

const SpinnerRed = ({ size = 28 }) => (
    <div
        style={{ width: size, height: size }}
        className="inline-block rounded-full border-4 border-red-200 border-t-red-600 animate-spin align-middle"
    />
);

/** Tarjeta KPI */
function StatCard({ title, value, icon: Icon, accent = "", active = false, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`w-full rounded-2xl border p-4 text-left shadow-sm transition
        ${active ? "border-red-300 ring-2 ring-red-200" : "border-neutral-200"}
        ${onClick ? "hover:shadow-md active:scale-[0.99] cursor-pointer" : ""}
        bg-white`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm text-neutral-500">{title}</div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
                </div>
                <div className={`rounded-xl p-2 ${accent || "bg-red-50"}`}>
                    <Icon className="h-5 w-5 text-red-600" />
                </div>
            </div>
        </button>
    );
}

/** Encabezado con ordenamiento */
function ThSort({ label, active, dir, onClick }) {
    const Icon = active ? (dir === "asc" ? ChevronUp : ChevronDown) : ArrowUpDown;
    return (
        <th className="p-3">
            <button
                type="button"
                onClick={onClick}
                className={`inline-flex items-center gap-1 rounded px-1 py-0.5 transition ${active ? "bg-neutral-200/60" : "hover:bg-neutral-200/40"
                    }`}
                title={`Ordenar por ${label}`}
            >
                <span>{label}</span>
                <Icon className="h-4 w-4" />
            </button>
        </th>
    );
}

/* ===== Helpers de cálculo (mismo criterio que en cliente) ===== */
const lineSubtotal = (p) => {
    const precio = Number(p.precioUnitario || 0);
    const bonif = Number(p.bonificacion || 0) / 100; // %
    const qty = Number(p.cantidad || p.qty || 0);
    return Number((qty * precio * (1 - bonif)).toFixed(2));
};

function computeTotalsFromDoc(d) {
    const items = Array.isArray(d.productos) ? d.productos : [];
    const originalTotal = items.reduce((a, p) => a + lineSubtotal({ ...p, cantidad: Number(p.baseQty ?? p.cantidad ?? 0) }), 0);
    const subtotal = items.reduce((a, p) => a + lineSubtotal(p), 0);

    const iibbCabaRate =
        d?.aliquotaCABA != null ? Number(d.aliquotaCABA) / 100 : IIBB_CABA_RATE_DEFAULT;
    const iibbArbaRate =
        d?.aliquotaARBA != null ? Number(d.aliquotaARBA) / 100 : IIBB_ARBA_RATE_DEFAULT;

    const promptDiscount = Math.round(subtotal * PROMPT_PAY_RATE * 100) / 100;
    const discountedSubtotal = Math.max(0, Math.round((subtotal - promptDiscount) * 100) / 100);

    const iibbCaba = Math.round(discountedSubtotal * iibbCabaRate * 100) / 100;
    const iibbArba = Math.round(discountedSubtotal * iibbArbaRate * 100) / 100;
    const iva = Math.round(discountedSubtotal * IVA_RATE * 100) / 100;

    const total = Math.round((discountedSubtotal + iibbCaba + iibbArba + iva) * 100) / 100;

    return {
        originalTotal: Number(originalTotal.toFixed(2)),
        subtotal: Number(subtotal.toFixed(2)),
        promptDiscount: Number(promptDiscount.toFixed(2)),
        discountedSubtotal: Number(discountedSubtotal.toFixed(2)),
        iibbCaba: Number(iibbCaba.toFixed(2)),
        iibbArba: Number(iibbArba.toFixed(2)),
        iva: Number(iva.toFixed(2)),
        total: Number(total.toFixed(2)),
        rates: {
            iibbCaba: iibbCabaRate,
            iibbArba: iibbArbaRate,
            iva: IVA_RATE,
            promptPay: PROMPT_PAY_RATE,
        },
    };
}

/** Crea doc en pendingOrdersPaid si no existe ya */
async function ensurePendingOrdersPaid(orderId) {
    const ref = doc(db, "clientToInvestigateOrders", orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Pedido no encontrado");
    const d = { id: snap.id, ...snap.data() };

    const existsSnap = await getDocs(
        query(collection(db, "pendingOrdersPaid"), where("sourceDocId", "==", orderId))
    );
    if (!existsSnap.empty) {
        return { created: false, totals: d?.totals || null };
    }

    const totals = computeTotalsFromDoc(d);

    const payload = {
        sourceDocId: orderId,
        createdAt: new Date(),
        status: "paid",
        idCliente: d?.idCliente ?? null,
        razonSocial: d?.razonSocial ?? null,
        cuit: d?.cuit ?? null,
        template: d?.template ?? null,
        numeroPedido: d?.numeroPedido ?? null,
        productos: (Array.isArray(d.productos) ? d.productos : []).map((p) => ({
            codigo: p.codigo,
            descripcion: p.descripcion,
            cantidad: Number(p.cantidad ?? p.qty ?? 0),
            precioUnitario: Number(p.precioUnitario ?? 0),
            bonificacion: Number(p.bonificacion ?? 0),
            subtotal: lineSubtotal(p),
        })),
        totals: {
            originalTotal: totals.originalTotal,
            subtotal: totals.subtotal,
            promptDiscount: totals.promptDiscount,
            discountedSubtotal: totals.discountedSubtotal,
            iibbCaba: totals.iibbCaba,
            iibbArba: totals.iibbArba,
            iva: totals.iva,
            total: totals.total,
            totalToTransfer: totals.total,
        },
        rates: totals.rates,
        payment: {
            method: "manual-dashboard",
            markedAt: new Date(),
        },
    };

    await addDoc(collection(db, "pendingOrdersPaid"), payload);
    return { created: true, totals };
}

/* ---------- Popover de acciones por fila ---------- */
function useClickAway(ref, onAway) {
    useEffect(() => {
        function onDocClick(e) {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) onAway?.();
        }
        function onEsc(e) {
            if (e.key === "Escape") onAway?.();
        }
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onEsc);
        };
    }, [ref, onAway]);
}

function ActionMenu({ disabled, currentStatus, onAction }) {
    const [open, setOpen] = useState(false);
    const [confirmFor, setConfirmFor] = useState(null);
    const ref = useRef(null);
    useClickAway(ref, () => {
        setOpen(false);
        setConfirmFor(null);
    });

    const itemClass =
        "w-full text-left px-3 py-2 rounded-md hover:bg-neutral-100 text-sm transition disabled:opacity-50 disabled:cursor-not-allowed";
    const isPaid = currentStatus === "paid";
    const isCommit = currentStatus === "pendingPaymentCash";
    const isCancelled = currentStatus === "cancelled";

    const labels = {
        paid: "Marcar como Pagado",
        pendingPaymentCash: "Compromiso de Pago (transferencia)",
        cancelled: "Cancelar pedido",
    };

    return (
        <div className="relative inline-block text-left" ref={ref}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 text-sm hover:bg-neutral-50 disabled:opacity-50"
                title="Acciones"
            >
                <MoreVertical className="h-4 w-4" />
                Acciones
            </button>

            {open && (
                <div className="absolute right-0 mt-1 w-64 origin-top-right rounded-xl border border-neutral-200 bg-white p-2 shadow-lg z-20">
                    {!confirmFor ? (
                        <div className="space-y-1">
                            <button
                                className={itemClass}
                                disabled={disabled || isPaid}
                                onClick={() => setConfirmFor("paid")}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                    {labels.paid}
                                </span>
                            </button>
                            <button
                                className={itemClass}
                                disabled={disabled || isCommit}
                                onClick={() => setConfirmFor("pendingPaymentCash")}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                                    {labels.pendingPaymentCash}
                                </span>
                            </button>
                            <button
                                className={itemClass + " text-red-600 hover:bg-red-50"}
                                disabled={disabled || isCancelled}
                                onClick={() => setConfirmFor("cancelled")}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <XCircle className="h-4 w-4" />
                                    {labels.cancelled}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="p-2 rounded-lg bg-neutral-50 border border-neutral-200">
                            <div className="text-sm font-medium mb-2">
                                ¿Confirmar <span className="underline">{labels[confirmFor]}</span>?
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <button
                                    className="px-3 py-1 rounded-md border hover:bg-white text-sm"
                                    onClick={() => setConfirmFor(null)}
                                >
                                    Volver
                                </button>
                                <button
                                    className={`px-3 py-1 rounded-md text-sm text-white ${confirmFor === "cancelled"
                                        ? "bg-red-600 hover:bg-red-700"
                                        : confirmFor === "paid"
                                            ? "bg-emerald-600 hover:bg-emerald-700"
                                            : "bg-amber-600 hover:bg-amber-700"
                                        }`}
                                    onClick={async () => {
                                        await onAction(confirmFor);
                                        setOpen(false);
                                        setConfirmFor(null);
                                    }}
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ===== Modal pagos ===== */
function DetailModal({ open, onClose, data, loading }) {
    if (!open) return null;

    const title = data?.numeroPedido ? `Pedido pagado — Nº ${data.numeroPedido}` : "Pedido pagado";
    const totals = data?.totals || {};
    const items = Array.isArray(data?.productos) ? data.productos : [];

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="w-full max-w-3xl h-[80vh] rounded-2xl bg-white shadow-2xl border border-neutral-200 flex flex-col">
                    <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                        <h3 className="text-lg font-semibold">{title}</h3>
                        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700">
                            ✕
                        </button>
                    </div>
                    <div className="px-5 py-4 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center gap-3">
                                <SpinnerRed />
                                <div className="text-neutral-700 text-sm">Cargando detalle…</div>
                            </div>
                        ) : !data ? (
                            <div className="text-sm text-red-700">No se encontró el detalle del pago.</div>
                        ) : (
                            <>
                                <div className="grid md:grid-cols-2 gap-3 mb-4">
                                    <div className="rounded-xl border p-3">
                                        <div className="text-xs text-neutral-500">Cliente</div>
                                        <div className="font-medium break-words">{data.razonSocial || "—"}</div>
                                        <div className="text-xs text-neutral-500 mt-1">CUIT: {data.cuit || "—"}</div>
                                        {data.sourceDocId ? (
                                            <div className="text-xs text-neutral-500 mt-1">Order ID: {data.sourceDocId}</div>
                                        ) : null}
                                    </div>
                                    <div className="rounded-xl border p-3">
                                        <div className="text-xs text-neutral-500">Pago</div>
                                        <div className="text-sm">
                                            <b>Método:</b> {data?.payment?.method || "—"}
                                        </div>
                                        <div className="text-sm">
                                            <b>Fecha:</b> {data.paidAt ? formatDateAR(data.paidAt) : "—"}
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-2xl border bg-neutral-50 p-3 mb-4">
                                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                                        {"subtotal" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">Sub-total</div>
                                                <div className="text-base font-semibold">{nfARS.format(Number(totals.subtotal || 0))}</div>
                                            </div>
                                        )}
                                        {"promptDiscount" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">Descuento pronto pago</div>
                                                <div className="text-base font-semibold">− {nfARS.format(Number(totals.promptDiscount || 0))}</div>
                                            </div>
                                        )}
                                        {"discountedSubtotal" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">Sub-total con descuento</div>
                                                <div className="text-base font-semibold">{nfARS.format(Number(totals.discountedSubtotal || 0))}</div>
                                            </div>
                                        )}
                                        {"iibbCaba" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">IIBB CABA</div>
                                                <div className="text-base font-semibold">{nfARS.format(Number(totals.iibbCaba || 0))}</div>
                                            </div>
                                        )}
                                        {"iibbArba" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">IIBB ARBA</div>
                                                <div className="text-base font-semibold">{nfARS.format(Number(totals.iibbArba || 0))}</div>
                                            </div>
                                        )}
                                        {"iva" in totals && (
                                            <div className="rounded-xl border bg-white p-3">
                                                <div className="text-xs text-neutral-500">IVA</div>
                                                <div className="text-base font-semibold">{nfARS.format(Number(totals.iva || 0))}</div>
                                            </div>
                                        )}
                                        <div className="rounded-xl border bg-white p-3 sm:col-span-2 md:col-span-3">
                                            <div className="text-xs text-neutral-500">Total</div>
                                            <div className="text-lg font-extrabold">
                                                {nfARS.format(Number(totals.total ?? totals.totalToTransfer ?? 0))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-2xl border overflow-hidden">
                                    <div className="bg-neutral-50 px-3 py-2 text-sm font-medium">Ítems</div>
                                    <div className="overflow-auto">
                                        {items.length ? (
                                            <table className="w-full text-sm">
                                                <thead className="bg-neutral-50">
                                                    <tr>
                                                        <th className="text-left p-2">Código</th>
                                                        <th className="text-left p-2">Descripción</th>
                                                        <th className="text-right p-2">Cantidad</th>
                                                        <th className="text-right p-2">Precio</th>
                                                        <th className="text-right p-2">Bonif</th>
                                                        <th className="text-right p-2">Subtotal</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((p, i) => (
                                                        <tr key={i} className="border-t">
                                                            <td className="p-2">{p.codigo}</td>
                                                            <td className="p-2 break-words">{p.descripcion}</td>
                                                            <td className="p-2 text-right">{Number(p.cantidad ?? p.qty ?? 0)}</td>
                                                            <td className="p-2 text-right">{nfARS.format(Number(p.precioUnitario || 0))}</td>
                                                            <td className="p-2 text-right">{`${Number(p.bonificacion || 0)}%`}</td>
                                                            <td className="p-2 text-right">
                                                                {nfARS.format(
                                                                    Number(
                                                                        p.subtotal ??
                                                                        (Number(p.precioUnitario || 0) *
                                                                            Number(p.cantidad || p.qty || 0) *
                                                                            (1 - Number(p.bonificacion || 0) / 100))
                                                                    )
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="p-4 text-sm text-neutral-600">No hay ítems para mostrar.</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ===== Modal historial (sirve para global y por pedido) ===== */
function OutboundHistoryModal({ open, onClose, title, items, loading, showOrderLink = false }) {
    if (!open) return null;

    const renderWhen = (it) => {
        const t = toMs(it?.sentAt) || toMs(it?.createdAt) || toMs(it?.lastAttemptAt) || toMs(it?.lastErrorAt) || null;
        return t ? formatDateAR(t) : "—";
    };

    const renderOrderId = (it) => String(it?.clientDocId || it?.sourceDocId || it?.orderId || "");

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="w-full max-w-4xl h-[80vh] rounded-2xl bg-white shadow-2xl border border-neutral-200 flex flex-col">
                    <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                        <h3 className="text-lg font-semibold">{title || "Historial de envíos"}</h3>
                        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700">✕</button>
                    </div>

                    <div className="px-5 py-4 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center gap-3">
                                <SpinnerRed />
                                <div className="text-neutral-700 text-sm">Cargando historial…</div>
                            </div>
                        ) : !items?.length ? (
                            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
                                No hay historial para mostrar.
                                <div className="mt-2 text-xs text-neutral-500">
                                    Tip: asegurate de que <b>ordersOutboundHistory</b> tenga documentos con <b>sentAt</b> y <b>clientDocId</b>.
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {items.map((it) => {
                                    const status = String(it.status || it?.statusRaw || "sent");
                                    const to = String(it.to || it.number || "—");
                                    const razon = String(it.razonSocial || "—");
                                    const tplLogical = it.templateLogical || it.template || "—";
                                    const tplInfobip = it.templateInfobip || it.templateName || "—";
                                    const source = it._source || "ordersOutboundHistory";
                                    const when = renderWhen(it);
                                    const link = it.link || null;
                                    const reason = it.lastErrorReason || it.lastError || null;
                                    const orderId = renderOrderId(it);

                                    return (
                                        <div key={it.id} className="rounded-2xl border border-neutral-200 bg-white p-4">
                                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                                <div className="text-sm font-semibold text-neutral-900">{when}</div>
                                                <div className="text-xs text-neutral-500">
                                                    Fuente: <span className="font-medium">{source}</span>
                                                </div>
                                            </div>

                                            <div className="mt-2 grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                                <div>
                                                    <div className="text-xs text-neutral-500">Cliente</div>
                                                    <div className="font-medium break-words">{razon}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-neutral-500">Para</div>
                                                    <div className="font-medium tabular-nums">{to}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-neutral-500">Estado</div>
                                                    <div className="font-medium">{status}</div>
                                                </div>

                                                {showOrderLink && orderId ? (
                                                    <div className="md:col-span-3">
                                                        <div className="text-xs text-neutral-500">Pedido</div>
                                                        <a
                                                            href={`/pendingorders/${orderId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-sm text-neutral-800 underline hover:text-red-700"
                                                        >
                                                            <ExternalLink className="h-4 w-4" />
                                                            {orderId}
                                                        </a>
                                                    </div>
                                                ) : null}

                                                <div>
                                                    <div className="text-xs text-neutral-500">Template (lógico)</div>
                                                    <div className="font-medium">{String(tplLogical)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-neutral-500">Template (Infobip)</div>
                                                    <div className="font-medium">{String(tplInfobip)}</div>
                                                </div>
                                            </div>

                                            {reason ? (
                                                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                                    <b>Error:</b> {String(reason)}
                                                </div>
                                            ) : null}

                                            {link ? (
                                                <div className="mt-3">
                                                    <a
                                                        href={link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-sm text-neutral-700 hover:bg-neutral-50"
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                        Abrir link
                                                    </a>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="px-5 py-3 border-t bg-white flex items-center justify-end">
                        <button onClick={onClose} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm hover:bg-neutral-50">
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ==================== Screen ==================== */
export default function PendingBillingScreen() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [sumSent, setSumSent] = useState(0);
    const [sumPendingPay, setSumPendingPay] = useState(0);
    const [sumPaid, setSumPaid] = useState(0);
    const [sumCanceled, setSumCanceled] = useState(0);
    const [sumAnuladoPorTiempo, setSumAnuladoPorTiempo] = useState(0);
    const [sumReviewToCheckout, setSumReviewToCheckout] = useState(0);
    const [error, setError] = useState("");

    // Selección única de KPI: null = todos
    const [selectedKind, setSelectedKind] = useState(null);

    // Ordenamiento
    const [sortKey, setSortKey] = useState("total");
    const [sortDir, setSortDir] = useState("desc");

    // Acción por fila
    const [actingId, setActingId] = useState(null);

    // Modal pagos
    const [paidDetailOpen, setPaidDetailOpen] = useState(false);
    const [paidDetailData, setPaidDetailData] = useState(null);
    const [paidDetailLoading, setPaidDetailLoading] = useState(false);

    // Modal historial (reutilizable)
    const [histOpen, setHistOpen] = useState(false);
    const [histLoading, setHistLoading] = useState(false);
    const [histItems, setHistItems] = useState([]);
    const [histTitle, setHistTitle] = useState("Historial de envíos");
    const [histShowOrderLink, setHistShowOrderLink] = useState(false);

    async function openPaidDetail(row) {
        try {
            setPaidDetailLoading(true);
            setPaidDetailOpen(true);
            setPaidDetailData(null);

            const refPaid = doc(db, "pendingOrdersPaid", row.id);
            const snapPaid = await getDoc(refPaid);
            if (!snapPaid.exists()) {
                toast.error("No se encontró el pago.");
                setPaidDetailLoading(false);
                return;
            }
            const paid = { id: snapPaid.id, ...snapPaid.data() };

            let productos = Array.isArray(paid.productos) ? paid.productos : [];
            let razonSocial = paid.razonSocial || "";
            let cuit = paid.cuit || "";
            if ((!productos.length || !razonSocial || !cuit) && paid.sourceDocId) {
                try {
                    const refOrder = doc(db, "clientToInvestigateOrders", paid.sourceDocId);
                    const snapOrder = await getDoc(refOrder);
                    if (snapOrder.exists()) {
                        const od = snapOrder.data() || {};
                        if (!productos.length && Array.isArray(od.productos)) productos = od.productos;
                        if (!razonSocial) razonSocial = od.razonSocial || razonSocial;
                        if (!cuit) cuit = od.cuit || cuit;
                    }
                } catch { }
            }

            const totals = derivePaidTotals({ ...paid, productos });
            const paidAt = getPaidAt(paid);

            setPaidDetailData({
                ...paid,
                productos,
                razonSocial,
                cuit,
                totals,
                paidAt,
            });
        } catch (err) {
            console.error(err);
            toast.error("No se pudo cargar el detalle del pago.");
        } finally {
            setPaidDetailLoading(false);
        }
    }

    // 🔥 Historial por pedido (por fila)
    async function openOutboundHistoryForRow(row) {
        const orderId = row.source === "paid" ? (row.detailId || "") : (row.id || "");
        if (!orderId) {
            toast.error("No hay orderId para buscar historial.");
            return;
        }

        setHistOpen(true);
        setHistLoading(true);
        setHistItems([]);
        setHistTitle(row.razonSocial ? `Historial — ${row.razonSocial}` : "Historial de envíos");
        setHistShowOrderLink(true);

        try {
            const q1 = query(
                collection(db, "clientsToSendOrders"),
                where("clientDocId", "==", orderId),
                where("status", "==", "sent")
            );

            const snap = await getDocs(q1);

            const items = snap.docs.map((d) => {
                const x = d.data() || {};
                return {
                    id: d.id,
                    sentAt: x.sentAt || x?.sentInfo?.sentAt || x.createdAt || null,
                    to: x.number || x?.sentInfo?.to || null,
                    templateLogical: x.template || x?.sentInfo?.templateLogical || null,
                    templateInfobip: x?.sentInfo?.templateInfobip || null,
                    link: x.link || x?.sentInfo?.link || null,
                    clientDocId: x.clientDocId || x?.sentInfo?.clientDocId || null,
                    razonSocial: x.razonSocial || x?.sentInfo?.razonSocial || null,
                    idCliente: x.idCliente || x?.sentInfo?.idCliente || null,
                    _source: "clientsToSendOrders",
                };
            });

            items.sort((a, b) => Number(toMs(b.sentAt) || 0) - Number(toMs(a.sentAt) || 0));
            setHistItems(items);
        } catch (err) {
            console.error(err);
            toast.error("No se pudo cargar el historial del pedido.");
        } finally {
            setHistLoading(false);
        }
    }


    async function openGlobalOutboundHistory() {
        setHistOpen(true);
        setHistLoading(true);
        setHistItems([]);
        setHistTitle("Historial global — mensajes enviados (más recientes primero)");
        setHistShowOrderLink(true);

        try {
            const q1 = query(
                collection(db, "clientsToSendOrders"),
                where("status", "==", "sent")
                // Si después querés orden real en server:
                // orderBy("sentAt", "desc"),
                // limit(500)
            );

            const snap = await getDocs(q1);

            const items = snap.docs.map((d) => {
                const x = d.data() || {};
                return {
                    id: d.id,
                    sentAt: x.sentAt || x?.sentInfo?.sentAt || x.createdAt || null,
                    to: x.number || x?.sentInfo?.to || null,
                    templateLogical: x.template || x?.sentInfo?.templateLogical || null,
                    templateInfobip: x?.sentInfo?.templateInfobip || null,
                    link: x.link || x?.sentInfo?.link || null,
                    clientDocId: x.clientDocId || x?.sentInfo?.clientDocId || null,
                    razonSocial: x.razonSocial || x?.sentInfo?.razonSocial || null,
                    idCliente: x.idCliente || x?.sentInfo?.idCliente || null,
                    _source: "clientsToSendOrders",
                };
            });

            // Orden local “best effort”
            items.sort((a, b) => Number(toMs(b.sentAt) || 0) - Number(toMs(a.sentAt) || 0));

            setHistItems(items.slice(0, 500));
        } catch (err) {
            console.error(err);
            toast.error("No se pudo cargar el historial global.");
        } finally {
            setHistLoading(false);
        }
    }


    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const selectKpi = (kind) => {
        if (kind === "all") setSelectedKind(null);
        else setSelectedKind(kind);
    };

    const recomputeKpis = (list) => {
        const totalEnviados = list.reduce((acc, r) => acc + (r.total || 0), 0);
        let pendingPay = 0,
            paid = 0,
            canceled = 0,
            anuladoPorTiempo = 0,
            reviewToCheckout = 0;
        for (const r of list) {
            if (r.kind === "pendingPay") pendingPay += r.total || 0;
            else if (r.kind === "paid") paid += r.total || 0;
            else if (r.kind === "canceled") canceled += r.total || 0;
            else if (r.kind === "anuladoPorTiempo") anuladoPorTiempo += r.total || 0;
            else if (r.kind === "reviewToCheckout") reviewToCheckout += r.total || 0;
        }
        setSumSent(totalEnviados);
        setSumPendingPay(pendingPay);
        setSumPaid(paid);
        setSumCanceled(canceled);
        setSumAnuladoPorTiempo(anuladoPorTiempo);
        setSumReviewToCheckout(reviewToCheckout);
    };

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            /* 1) Leer TODOS los envíos y construir índice por CUIT con dedupe */
            const allSendsSnap = await getDocs(collection(db, "clientsToSendOrders"));
            const numbersByCuit = new Map();

            for (const docu of allSendsSnap.docs) {
                const d = docu.data() || {};
                const cuit = onlyDigits(d.cuit || d.CUIT || "");
                if (!cuit) continue;

                const acc = numbersByCuit.get(cuit) || { entries: [], hasSent: false };
                const seen = new Set(acc.entries.map((e) => e.clean));

                const candidates = [
                    ...(d.number ? [String(d.number)] : []),
                    ...(Array.isArray(d.numbers) ? d.numbers.map(String) : []),
                ];
                for (const raw of candidates) {
                    const v = validateWaNumber(raw);
                    if (seen.has(v.clean)) continue;
                    seen.add(v.clean);
                    acc.entries.push({
                        raw,
                        clean: v.clean,
                        valid: v.valid,
                        errors: v.errors,
                        status: String(d.status || "unknown"),
                        docId: docu.id,
                    });
                    if (String(d.status).toLowerCase() === "sent" && v.valid) acc.hasSent = true;
                }
                numbersByCuit.set(cuit, acc);
            }

            /* 2) UNPAID */
            const unpaidRows = [];
            const snapCli = await getDocs(collection(db, "clientToInvestigateOrders"));
            for (const docu of snapCli.docs) {
                const d = docu.data() || {};
                const st = String(d.status || "");
                if (["paid", "approved", "completed"].includes(st.toLowerCase())) continue;

                const baseTotal = Number(d.total || 0);
                const totalConIva = baseTotal * (1 + IVA_RATE);

                const templateStr = String(d.template || "").toLowerCase();
                const isReviewToCheckout = templateStr.includes("reviewtocheckout");

                const include = isReviewToCheckout ? true : totalConIva > THRESHOLD;
                if (!include) continue;

                const displayStatus = isReviewToCheckout ? "reviewToCheckout" : st;

                const lastStatusAt = getLastStatusAt(d);
                const ui = mapStatusUI(displayStatus);
                const cuitNorm = onlyDigits(d.cuit || "");
                const sendInfo = numbersByCuit.get(cuitNorm) || { entries: [], hasSent: false };

                const deduped = [];
                const seenClean = new Set();
                for (const e of sendInfo.entries) {
                    if (seenClean.has(e.clean)) continue;
                    seenClean.add(e.clean);
                    deduped.push(e);
                }

                unpaidRows.push({
                    id: docu.id,
                    detailId: docu.id,
                    cuit: String(d.cuit || ""),
                    razonSocial: d.razonSocial || "",
                    total: baseTotal,
                    totalConIva,
                    status: displayStatus,
                    kind: ui.kind,
                    numeroPedido: d.numeroPedido || "",
                    lastStatusAt,
                    path: d.path || "",
                    numbersInfo: deduped,
                    hasSent: sendInfo.hasSent,
                    source: "investigate",
                });
            }

            /* 3) PAID */
            const paidRows = [];
            const paidIds = new Set();
            const qPaidAll = query(collection(db, "pendingOrdersPaid"), where("status", "==", "paid"));
            const snapPaidAll = await getDocs(qPaidAll);

            for (const docPaid of snapPaidAll.docs) {
                if (paidIds.has(docPaid.id)) continue;
                const d = docPaid.data() || {};
                const totalPaid = getPaidTotal(d);
                const totalConIva = Number(totalPaid || 0) * (1 + IVA_RATE);

                let cuit = String(d.cuit || "");
                let razonSocial = d.razonSocial || "";
                if ((!cuit || !razonSocial) && d.sourceDocId) {
                    try {
                        const refOrder = doc(db, "clientToInvestigateOrders", d.sourceDocId);
                        const snapOrder = await getDoc(refOrder);
                        if (snapOrder.exists()) {
                            const od = snapOrder.data() || {};
                            if (!cuit) cuit = String(od.cuit || "");
                            if (!razonSocial) razonSocial = od.razonSocial || razonSocial;
                        }
                    } catch { }
                }

                const cuitNorm = onlyDigits(cuit || "");
                const sendInfo = numbersByCuit.get(cuitNorm) || { entries: [], hasSent: false };

                const deduped = [];
                const seenClean = new Set();
                for (const e of sendInfo.entries) {
                    if (seenClean.has(e.clean)) continue;
                    seenClean.add(e.clean);
                    deduped.push(e);
                }

                paidIds.add(docPaid.id);
                paidRows.push({
                    id: docPaid.id,
                    detailId: d.sourceDocId || "",
                    cuit,
                    razonSocial,
                    total: Number(totalPaid || 0),
                    totalConIva,
                    status: "paid",
                    kind: "paid",
                    numeroPedido: d.numeroPedido || "",
                    lastStatusAt: getPaidLastAt(d),
                    path: "",
                    numbersInfo: deduped,
                    hasSent: sendInfo.hasSent,
                    source: "paid",
                });
            }

            /* 4) Evitar duplicados */
            const paidSourceIds = new Set(paidRows.map((r) => r.detailId).filter(Boolean));
            const unpaidFiltered = unpaidRows.filter((r) => !paidSourceIds.has(r.id));

            /* 5) Merge + KPIs */
            const merged = [...unpaidFiltered, ...paidRows];
            setRows(merged);
            recomputeKpis(merged);
        } catch (err) {
            console.error(err);
            setError(err?.message || "Error cargando datos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAllActive = selectedKind === null;
    const kpiActive = {
        pendingPay: selectedKind === "pendingPay",
        paid: selectedKind === "paid",
        canceled: selectedKind === "canceled",
        anuladoPorTiempo: selectedKind === "anuladoPorTiempo",
        reviewToCheckout: selectedKind === "reviewToCheckout",
    };

    const filterLabel =
        selectedKind === "pendingPay"
            ? "En espera de pago"
            : selectedKind === "paid"
                ? "Pagados"
                : selectedKind === "canceled"
                    ? "Cancelados"
                    : selectedKind === "anuladoPorTiempo"
                        ? "Anulados por tiempo"
                        : selectedKind === "reviewToCheckout"
                            ? "Pendientes de sumar productos"
                            : null;

    const visibleRows = useMemo(() => {
        let data = rows;
        if (selectedKind) data = data.filter((r) => r.kind === selectedKind);

        const getVal = (r) => {
            switch (sortKey) {
                case "razonSocial":
                    return (r.razonSocial || "").toString().toLowerCase();
                case "cuit":
                    return (r.cuit || "").toString();
                case "total":
                    return Number(r.total || 0);
                case "totalConIva":
                    return Number(r.totalConIva || (r.total || 0) * (1 + IVA_RATE));
                case "estado":
                    return mapStatusUI(r.status).label.toLowerCase();
                case "numeroPedido":
                    return String(r.numeroPedido || "");
                case "lastStatusAt":
                    return Number(r.lastStatusAt || 0);
                default:
                    return 0;
            }
        };

        const dir = sortDir === "asc" ? 1 : -1;
        const sorted = [...data].sort((a, b) => {
            const va = getVal(a);
            const vb = getVal(b);
            if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
            return va < vb ? -1 * dir : va > vb ? 1 * dir : 0;
        });

        return sorted;
    }, [rows, selectedKind, sortKey, sortDir]);

    const countRows = useMemo(() => visibleRows.length, [visibleRows]);

    const statusSuccessMsg = {
        paid: "Pedido marcado como Pagado. Se registró en pendingOrdersPaid.",
        pendingPaymentCash: "Pedido marcado con Compromiso de pago (transferencia).",
        cancelled: "Pedido cancelado.",
    };

    async function updateStatusAndMaybeCreatePaid(row, newStatus) {
        setActingId(row.id);
        try {
            const ref = doc(db, "clientToInvestigateOrders", row.id);
            const snap = await getDoc(ref);
            if (!snap.exists()) throw new Error("Pedido no encontrado");
            const d = { id: snap.id, ...snap.data() };

            const now = new Date();
            const history = Array.isArray(d.statusHistory) ? d.statusHistory.slice() : [];
            history.push({
                from: d.status ?? null,
                to: newStatus,
                at: now,
                by: "dashboard",
            });

            const updatePayload = {
                status: newStatus,
                statusUpdatedAt: now,
                updatedAt: now,
                statusHistory: history,
            };

            if (newStatus === "paid") {
                const { totals } = await ensurePendingOrdersPaid(row.id);
                if (totals?.total != null) updatePayload.finalAmountTransferred = Number(totals.total);
                if (d?.cuit && !d.cuitGuardado) {
                    updatePayload.cuitGuardado = true;
                }
            }

            await updateDoc(ref, updatePayload);

            setRows((prev) => {
                const next = prev.map((r) =>
                    r.id === row.id
                        ? {
                            ...r,
                            status: newStatus,
                            kind: mapStatusUI(newStatus).kind,
                            lastStatusAt: now.getTime(),
                        }
                        : r
                );
                recomputeKpis(next);
                return next;
            });

            toast.success(statusSuccessMsg[newStatus] || "Actualizado");
        } catch (err) {
            console.error(err);
            toast.error("No se pudo actualizar el estado");
        } finally {
            setActingId(null);
        }
    }

    return (
        <div className="min-h-screen bg-neutral-50 text-neutral-900">
            <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
                {/* Header */}
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                            Pedidos pendientes de facturación
                        </h1>
                        <div className="mt-1 text-sm text-neutral-500">
                            Umbral:&nbsp;
                            <span className="font-medium">{nfARS.format(THRESHOLD)}</span>
                            &nbsp;<span className="text-neutral-400">(aplica sobre total + IVA; los “Pendientes de sumar productos” se listan siempre)</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={openGlobalOutboundHistory}
                            className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-neutral-800 shadow-sm transition hover:bg-neutral-50 active:scale-[0.98]"
                            title="Ver historial global de mensajes enviados"
                        >
                            <History className="h-4 w-4" />
                            Historial global
                        </button>

                        <button
                            onClick={loadData}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-white shadow-sm transition hover:bg-red-700 active:scale-[0.98]"
                        >
                            <RefreshCw className="h-4 w-4" /> Actualizar
                        </button>
                    </div>
                </div>

                {/* KPIs */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3"
                >
                    <StatCard title="Total enviados" value={nfARS.format(sumSent)} icon={FileText} active={isAllActive} onClick={() => selectKpi("all")} />
                    <StatCard title="Total en espera de pago" value={nfARS.format(sumPendingPay)} icon={AlertTriangle} accent="bg-amber-50" active={kpiActive.pendingPay} onClick={() => selectKpi("pendingPay")} />
                    <StatCard title="Total pagados" value={nfARS.format(sumPaid)} icon={CheckCircle2} accent="bg-green-50" active={kpiActive.paid} onClick={() => selectKpi("paid")} />
                    <StatCard title="Total cancelados" value={nfARS.format(sumCanceled)} icon={XCircle} accent="bg-red-50" active={kpiActive.canceled} onClick={() => selectKpi("canceled")} />
                    <StatCard title="Total anulados por tiempo" value={nfARS.format(sumAnuladoPorTiempo)} icon={XCircle} accent="bg-purple-50" active={kpiActive.anuladoPorTiempo} onClick={() => selectKpi("anuladoPorTiempo")} />
                    <StatCard title="Pendientes de sumar productos" value={nfARS.format(sumReviewToCheckout)} icon={AlertTriangle} accent="bg-blue-50" active={kpiActive.reviewToCheckout} onClick={() => selectKpi("reviewToCheckout")} />
                </motion.div>

                {/* Body */}
                <div className="mt-6">
                    {loading ? (
                        <div className="flex h-48 items-center justify-center gap-3 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                            <SpinnerRed /> <span className="text-neutral-600">Cargando…</span>
                        </div>
                    ) : error ? (
                        <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                            <AlertTriangle className="h-5 w-5" />
                            <span className="font-medium">{error}</span>
                        </div>
                    ) : (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                            <div className="mb-2 text-sm text-neutral-600">
                                Mostrando <strong>{countRows}</strong> pedidos. Los <strong>“Pendientes de sumar productos”</strong> se listan aunque no superen el umbral; al resto se aplica <strong>total+IVA &gt; {nfARS.format(THRESHOLD)}</strong>.
                                {filterLabel && (
                                    <span className="ml-2 rounded-full bg-neutral-200/60 px-2 py-0.5 text-xs text-neutral-700">
                                        Filtro: {filterLabel}
                                    </span>
                                )}
                            </div>

                            <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
                                <div className="max-h-[70vh] overflow-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="sticky top-0 z-10 bg-neutral-100/80 backdrop-blur">
                                            <tr className="text-left">
                                                <ThSort label="Razón social" active={sortKey === "razonSocial"} dir={sortDir} onClick={() => handleSort("razonSocial")} />
                                                <ThSort label="CUIT" active={sortKey === "cuit"} dir={sortDir} onClick={() => handleSort("cuit")} />
                                                <ThSort label="Total" active={sortKey === "total"} dir={sortDir} onClick={() => handleSort("total")} />
                                                <ThSort label="Total c/ IVA" active={sortKey === "totalConIva"} dir={sortDir} onClick={() => handleSort("totalConIva")} />
                                                <ThSort label="Estado" active={sortKey === "estado"} dir={sortDir} onClick={() => handleSort("estado")} />
                                                <ThSort label="N° Pedido" active={sortKey === "numeroPedido"} dir={sortDir} onClick={() => handleSort("numeroPedido")} />
                                                <ThSort label="Última actualización" active={sortKey === "lastStatusAt"} dir={sortDir} onClick={() => handleSort("lastStatusAt")} />
                                                <th className="p-3">Enviado a</th>
                                                <th className="p-3">Detalle</th>
                                                <th className="p-3">Historial</th>
                                                <th className="p-3">Acciones</th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-neutral-100">
                                            {visibleRows.map((r) => {
                                                const { label, className, Icon } = mapStatusUI(r.status);
                                                const disabled = actingId === r.id || r.status === "paid";
                                                return (
                                                    <tr key={r.id} className="hover:bg-neutral-50">
                                                        <td className="p-3">{r.razonSocial || "—"}</td>
                                                        <td className="p-3 tabular-nums">{r.cuit}</td>
                                                        <td className="p-3 font-medium tabular-nums">{nfARS.format(r.total || 0)}</td>
                                                        <td className="p-3 font-semibold tabular-nums">{nfARS.format(r.totalConIva ?? ((r.total || 0) * (1 + IVA_RATE)))}</td>
                                                        <td className="p-3">
                                                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${className}`}>
                                                                <Icon className="h-3.5 w-3.5" />
                                                                {label}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">{String(r.numeroPedido || "—")}</td>
                                                        <td className="p-3">{formatDateAR(r.lastStatusAt)}</td>

                                                        <td className="p-3">
                                                            {r.numbersInfo?.length ? (
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {r.numbersInfo.map((n, i) => {
                                                                        const isInvalid = !n.valid;
                                                                        const classes = isInvalid
                                                                            ? "border-red-200 bg-red-50 text-red-700"
                                                                            : "border-neutral-200 bg-white text-neutral-700";
                                                                        const title = isInvalid ? describeNumberErrors(n.errors) : "Número";
                                                                        const content = n.clean || n.raw || "—";

                                                                        return isInvalid ? (
                                                                            <span
                                                                                key={`${r.id}-n-${i}`}
                                                                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${classes}`}
                                                                                title={title}
                                                                            >
                                                                                <XCircle className="h-3.5 w-3.5" />
                                                                                <span className="tabular-nums">{content}</span>
                                                                            </span>
                                                                        ) : (
                                                                            <a
                                                                                key={`${r.id}-n-${i}`}
                                                                                href={`https://wa.me/${n.clean}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                title={title}
                                                                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${classes}`}
                                                                            >
                                                                                <span className="tabular-nums">{content}</span>
                                                                            </a>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <span className="text-neutral-400">—</span>
                                                            )}
                                                        </td>

                                                        <td className="p-3">
                                                            {r.source === "paid" ? (
                                                                <button
                                                                    onClick={() => openPaidDetail(r)}
                                                                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-neutral-700 hover:bg-neutral-50"
                                                                    title="Ver detalle del pago"
                                                                >
                                                                    <ExternalLink className="h-4 w-4" />
                                                                    Ver
                                                                </button>
                                                            ) : (
                                                                <a
                                                                    href={r.detailId ? `/pendingorders/${r.detailId}` : "#"}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-neutral-700 hover:bg-neutral-50"
                                                                    title="Abrir detalle en nueva pestaña"
                                                                >
                                                                    <ExternalLink className="h-4 w-4" />
                                                                    Ver
                                                                </a>
                                                            )}
                                                        </td>

                                                        <td className="p-3">
                                                            <button
                                                                onClick={() => openOutboundHistoryForRow(r)}
                                                                className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-neutral-700 hover:bg-neutral-50"
                                                                title="Ver historial de mensajes enviados"
                                                            >
                                                                <History className="h-4 w-4" />
                                                                Historial
                                                            </button>
                                                        </td>

                                                        <td className="p-3">
                                                            <div className="flex items-center gap-2">
                                                                <ActionMenu disabled={disabled} currentStatus={r.status} onAction={(status) => updateStatusAndMaybeCreatePaid(r, status)} />
                                                                {actingId === r.id && <SpinnerRed size={20} />}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {!visibleRows.length && (
                                                <tr>
                                                    <td className="p-10 text-center text-neutral-500" colSpan={11}>
                                                        No hay pedidos para mostrar.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <DetailModal
                        open={paidDetailOpen}
                        onClose={() => setPaidDetailOpen(false)}
                        data={paidDetailData}
                        loading={paidDetailLoading}
                    />

                    <OutboundHistoryModal
                        open={histOpen}
                        onClose={() => setHistOpen(false)}
                        title={histTitle}
                        items={histItems}
                        loading={histLoading}
                        showOrderLink={histShowOrderLink}
                    />
                </div>
            </div>
        </div>
    );
}
